import React, { useId, useState } from "react";

/**
 * Tooltip
 * WHAT: A small, accessible tooltip component that renders its content
 * in the DOM and connects it via aria-describedby to the trigger element.
 * WHY: Required by task 075 to provide accessible hover/focus hints on IconButton and other controls.
 * HOW: The component renders a wrapper that accepts a child (the trigger) and a tooltip text.
 * It ensures the tooltip content exists in DOM (important for screen readers) and
 * toggles visibility on hover/focus. It respects `prefers-reduced-motion` implicitly
 * by not animating DOM insertion here — animations are handled by CSS classes.
 */

interface TooltipProps {
  /** Visible tooltip text */
  text: string;
  /** Optional id for aria association */
  id?: string;
  /** Children must be a single interactive element (button/link) */
  children: React.ReactElement<ChildProps>;
}

interface ChildProps {
  onFocus?: (e: React.FocusEvent) => void;
  onBlur?: (e: React.FocusEvent) => void;
  onMouseEnter?: (e: React.MouseEvent) => void;
  onMouseLeave?: (e: React.MouseEvent) => void;
}

export function Tooltip({ text, id, children }: TooltipProps): React.ReactElement {
  const generatedId = useId();
  const tooltipId = id ?? `tooltip-${generatedId}`;
  const [visible, setVisible] = useState(false);

  // Build a props object with proper types for accessibility and interaction handlers
  const extraProps: {
    'aria-describedby': string;
    onFocus: (e: React.FocusEvent) => void;
    onBlur: (e: React.FocusEvent) => void;
    onMouseEnter: (e: React.MouseEvent) => void;
    onMouseLeave: (e: React.MouseEvent) => void;
  } = {
    'aria-describedby': tooltipId,
    onFocus: (e: React.FocusEvent) => {
      setVisible(true);
      const fn = children.props.onFocus;
      if (typeof fn === 'function') fn(e);
    },
    onBlur: (e: React.FocusEvent) => {
      setVisible(false);
      const fn = children.props.onBlur;
      if (typeof fn === 'function') fn(e);
    },
    onMouseEnter: (e: React.MouseEvent) => {
      setVisible(true);
      const fn = children.props.onMouseEnter;
      if (typeof fn === 'function') fn(e);
    },
    onMouseLeave: (e: React.MouseEvent) => {
      setVisible(false);
      const fn = children.props.onMouseLeave;
      if (typeof fn === 'function') fn(e);
    }
  };

  // Clone child and attach accessibility + interaction handlers
  const child = React.cloneElement(children, extraProps);

  return (
    <span className="relative inline-flex" style={{ lineHeight: 0 }}>
      {child}
      {/* Tooltip content kept in DOM for a11y; visibility controlled by CSS and `visible` state */}
      <span
        id={tooltipId}
        role="status"
        aria-hidden={!visible}
        className={
          "pointer-events-none absolute z-50 whitespace-nowrap rounded-md bg-gray-800 text-white text-sm px-2 py-1 transition-opacity duration-150 " +
          (visible ? "opacity-100" : "opacity-0")
        }
        style={{ transform: "translateY(-100%)", bottom: "100%", left: "50%", transformOrigin: "center" }}
      >
        {text}
      </span>
    </span>
  );
}
